import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Truck, Package, MapPin, ArrowSquareOut, CheckCircle, Clock, Warning } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_LABELS = {
  processing: { label: 'Processing', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  ready_to_ship: { label: 'Ready to ship', icon: Package, color: 'text-blue-700 bg-blue-50 border-blue-200' },
  shipped: { label: 'Shipped', icon: Truck, color: 'text-brand bg-brand/5 border-brand/30' },
  delivered: { label: 'Delivered', icon: CheckCircle, color: 'text-green-700 bg-green-50 border-green-200' },
  cancelled: { label: 'Cancelled', icon: Warning, color: 'text-red-700 bg-red-50 border-red-200' },
  on_hold: { label: 'On hold', icon: Warning, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  awaiting_stock: { label: 'Awaiting stock', icon: Clock, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  partially_shipped: { label: 'Partially shipped', icon: Truck, color: 'text-blue-700 bg-blue-50 border-blue-200' },
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_) {
    return iso;
  }
};

/**
 * PublicTracking — UNAUTHENTICATED customer-facing order status page.
 *
 * Route: /track/:trackingNumber
 * Backed by GET /api/track/{tracking_number}
 *
 * Shows: order number, status, carrier + carrier deep-link, items summary,
 * destination (city/postcode only), shipped_at, expected_delivery.
 * Never displays tenant info, internal IDs, or full street address.
 */
export default function PublicTracking() {
  const { trackingNumber } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/api/track/${encodeURIComponent(trackingNumber)}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) setError('not_found');
        else setError('generic');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [trackingNumber]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  const statusMeta = STATUS_LABELS[data?.status] || STATUS_LABELS.processing;
  const StatusIcon = statusMeta.icon;

  return (
    <div data-testid="public-tracking-page" className="min-h-screen bg-zinc-50 flex items-start justify-center p-4 sm:p-8">
      <div className="w-full max-w-2xl">
        {/* Brand strip */}
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 bg-zinc-950 flex items-center justify-center">
            <Package size={22} weight="fill" className="text-amber-400" />
          </div>
          <div>
            <div className="text-sm font-black uppercase tracking-widest text-zinc-900">Stockaris</div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-wider">Order tracking</div>
          </div>
        </div>

        {loading && (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <div className="animate-pulse text-zinc-400 text-sm uppercase tracking-widest">Looking up your order…</div>
          </div>
        )}

        {!loading && error === 'not_found' && (
          <div data-testid="tracking-not-found" className="bg-white border border-zinc-200 p-12 text-center">
            <Warning size={40} weight="fill" className="text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-black uppercase tracking-widest mb-2">Tracking number not found</h1>
            <p className="text-sm text-zinc-600">
              Double-check the tracking number <span className="font-mono">{trackingNumber}</span> — if your order shipped recently it may take up to 30 minutes to appear here.
            </p>
          </div>
        )}

        {!loading && error === 'generic' && (
          <div className="bg-white border border-zinc-200 p-12 text-center">
            <Warning size={40} weight="fill" className="text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-black uppercase tracking-widest mb-2">Something went wrong</h1>
            <p className="text-sm text-zinc-600">
              Please refresh the page in a moment.
            </p>
          </div>
        )}

        {!loading && data && (
          <>
            {/* Status hero card */}
            <div data-testid="tracking-status-hero" className={`border-2 p-6 sm:p-8 ${statusMeta.color}`}>
              <div className="flex items-center gap-4">
                <StatusIcon size={48} weight="fill" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-black uppercase tracking-widest opacity-70">Order {data.order_number}</div>
                  <div className="text-3xl font-black tracking-tight" data-testid="tracking-status-label">{statusMeta.label}</div>
                  {data.shipped_at && data.status === 'shipped' && (
                    <div className="text-xs mt-1 opacity-80">Shipped {formatDate(data.shipped_at)}</div>
                  )}
                  {data.delivered_at && data.status === 'delivered' && (
                    <div className="text-xs mt-1 opacity-80">Delivered {formatDate(data.delivered_at)}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Tracking detail */}
            <div className="bg-white border border-zinc-200 border-t-0">
              <div className="p-5 border-b border-zinc-100">
                <div className="text-[11px] font-black uppercase tracking-widest text-zinc-500 mb-1">Tracking number</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-lg font-bold" data-testid="tracking-number-label">{data.tracking_number}</span>
                  <Button onClick={copyLink} variant="outline" size="sm" data-testid="copy-tracking-link-btn">
                    {copied ? 'Copied!' : 'Copy link'}
                  </Button>
                  {data.carrier_tracking_url && (
                    <a
                      href={data.carrier_tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex"
                      data-testid="carrier-track-link"
                    >
                      <Button variant="outline" size="sm" className="text-brand border-brand/40">
                        Track on {(data.carrier || 'carrier').toUpperCase()} <ArrowSquareOut size={14} className="ml-1" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>

              {data.destination && (
                <div className="p-5 border-b border-zinc-100">
                  <div className="text-[11px] font-black uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
                    <MapPin size={12} weight="fill" /> Destination
                  </div>
                  <div className="text-sm" data-testid="tracking-destination">{data.destination}</div>
                </div>
              )}

              {data.events && data.events.length > 0 && (
                <div className="p-5 border-b border-zinc-100">
                  <div className="text-[11px] font-black uppercase tracking-widest text-zinc-500 mb-3">
                    Timeline ({data.events.length})
                  </div>
                  <ol data-testid="tracking-events-timeline" className="relative pl-4 border-l-2 border-zinc-200">
                    {data.events.map((ev, i) => (
                      <li key={i} className="relative pb-3 last:pb-0">
                        <span className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-white ${i === 0 ? 'bg-brand' : 'bg-zinc-300'}`} />
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="text-sm font-bold capitalize">{(ev.status || '').replace(/_/g, ' ')}</span>
                          {ev.location && <span className="text-xs text-zinc-500">· {ev.location}</span>}
                        </div>
                        {ev.description && <div className="text-xs text-zinc-600 mt-0.5">{ev.description}</div>}
                        {ev.occurred_at && (
                          <div className="text-[11px] text-zinc-400 mt-0.5">{formatDate(ev.occurred_at)}</div>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {data.items && data.items.length > 0 && (
                <div className="p-5">
                  <div className="text-[11px] font-black uppercase tracking-widest text-zinc-500 mb-3">Items ({data.items.length})</div>
                  <ul className="divide-y divide-zinc-100" data-testid="tracking-items-list">
                    {data.items.map((it, i) => (
                      <li key={i} className="flex items-center justify-between py-2 text-sm">
                        <span className="truncate flex-1 mr-2">{it.name}</span>
                        <span className="font-mono text-zinc-500">×{it.quantity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-6 text-center text-[11px] text-zinc-400 uppercase tracking-widest">
              Tracking by <Link to="/" className="text-zinc-600 hover:text-zinc-900">Stockaris WMS</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
